import { UpperCasePipe } from '@angular/common';
import { Component, OnDestroy, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ImageUrlPipe, InsightMarkdownPipe } from '@frontend/shared';
import { TranslocoModule, provideTranslocoScope } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { MediaService } from '../../content';
import { getFilterStateByKey } from '../../contributions/contributions-filter-states.constants';
import { reloadOnLocaleChange } from '../../language';
import { LoaderGlobeAnimationDetailComponent } from '../../loaders';
import { PageTitleService } from '../../navigation/title/title.service';
import { CommunityInput, CommunityInputError, CommunityInputKind } from '../community-input.model';
import { CommunityInputService } from '../community-input.service';
import { workRouteFor } from '../community-input-routes';

/** The icon of each kind of work, on the card that commits the reader. */
const KIND_ICONS: Record<CommunityInputKind, string> = {
  video_recording: 'fa-solid fa-video',
  insight_writing: 'fa-solid fa-pen-nib',
  feature_review: 'fa-solid fa-magnifying-glass',
};

/**
 * One community input: what it involves and what the volunteer would work on — and, for a reader
 * who qualifies, the button that commits them — the Ideas plan, decision 3.32.
 *
 * **Enough to decide, and the rest on demand** — the product owner, 2026-10-13. What's involved
 * comes first, as the page's lead. Then, each in its own box: the idea it helps turn into reality,
 * the cultures the reader may work on (chosen here, as large pills with flags), the subject most
 * likely to be handed out for the culture chosen — confirmed only when they commit, by what the
 * community needs then — and the language the work is done in. The task's description, its videos
 * and the instructions are behind "Show more"; the work screen shows them in full.
 *
 * **Who it is open to is not shown**: that is staff's configuration, and the backend no longer
 * serves it.
 *
 * **Committing happens here, not on the idea's page.** "I want to help" on an idea only opens
 * this page, so a volunteer decides knowing what they take on. Committing creates their delivery
 * at once, with no approval, and opens the work screen for the task.
 *
 * A member holding one of the input's roles who lacks another qualification — or a candidate
 * whose trials are used up — sees the page without the button, and who it is open to, and is told
 * nothing more. Anyone else never gets here: the backend answers "not found".
 *
 * **The idea the reader came from is in the address**, `?from=<idea>`, so it survives a refresh,
 * a bookmark or an email. The page loads with it: for an insight input taking the idea's
 * cultures it decides what is offered, and it goes back as `via` when the reader commits. Such an
 * input reached without an idea asks the reader to choose one of the ideas it serves, by link,
 * so the choice lands in the address too. With more than one culture to work on, the first is
 * chosen for the reader, who may choose another before committing.
 */
@Component({
  selector: 'multisite-input-detail',
  templateUrl: './input-detail.component.html',
  styleUrl: './input-detail.component.scss',
  imports: [RouterModule, TranslocoModule, UpperCasePipe, InsightMarkdownPipe, ImageUrlPipe, LoaderGlobeAnimationDetailComponent],
  providers: [provideTranslocoScope('inputs'), provideTranslocoScope('contributions')],
})
export class InputDetailComponent implements OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private inputs = inject(CommunityInputService);
  private pageTitleService = inject(PageTitleService);
  private media = inject(MediaService);

  readonly heroTransform = 'w_1200,c_limit';

  input = signal<CommunityInput | null>(null);
  failure = signal<CommunityInputError | null>(null);
  actionFailure = signal<CommunityInputError | null>(null);
  loading = signal(false);
  taking = signal(false);
  /** The culture the reader chose, when more than one is offered. */
  chosenCulture = signal<string | null>(null);
  /** The task's description and the instructions, hidden until the reader asks for them. */
  showDetails = signal(false);
  /** The idea the reader came from, as its "I want to help" card links here. */
  readonly from = signal<string | null>(null);

  /** An input taking the idea's cultures, reached without an idea: the reader picks one first. */
  readonly needsIdea = computed(() => {
    const input = this.input();
    return !!input && input.cultureSource === 'idea' && !input.from && !input.myDelivery;
  });

  /**
   * The culture to commit with: the reader's choice, or else the first offered — primary cultures
   * come first — so the page shows a likely subject straight away.
   */
  readonly chosen = computed(() => {
    const candidates = this.input()?.candidateCultures ?? [];
    return candidates.find((culture) => culture.slug === this.chosenCulture()) ?? candidates[0] ?? null;
  });

  readonly kindIcon = computed(() => KIND_ICONS[this.input()?.kind ?? 'feature_review']);

  readonly hasDetails = computed(() => {
    const input = this.input();
    return !!input && !!(input.task.description || input.instructions.videos.length || input.instructions.particular.length || input.instructions.general.length);
  });

  /** How the reader's delivery state looks: the contributions area's own presentation. */
  readonly myState = computed(() => {
    const state = this.input()?.myDelivery?.state;
    return state ? getFilterStateByKey(state) ?? null : null;
  });

  private slug = this.route.snapshot.paramMap.get('slug') ?? '';
  private subscriptions: Subscription[] = [];

  constructor() {
    // Choosing an idea on this page changes only the address, so the page follows it.
    this.subscriptions.push(
      this.route.queryParamMap.subscribe((params) => {
        this.from.set(params.get('from'));
        this.load();
      }),
    );
    reloadOnLocaleChange(() => this.load());
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /** A round flag, or the platform's "missing" flag when there is none. */
  flagUrl(hash: string | null): string {
    return this.media.getFlagUrlFromHash(hash ?? '', '', true, '.png');
  }

  take(): void {
    const input = this.input();
    const culture = this.chosen()?.slug ?? null;

    if (!input?.can.take || this.taking() || (input.kind !== 'feature_review' && !culture)) {
      return;
    }

    this.taking.set(true);
    this.actionFailure.set(null);

    this.subscriptions.push(
      this.inputs
        .take(input.slug, input.from, culture)
        .pipe(finalize(() => this.taking.set(false)))
        .subscribe({
          next: (taken) => {
            this.input.set(taken);
            this.goToWork();
          },
          error: (failure: CommunityInputError) => {
            this.actionFailure.set(failure);
            this.load();
          },
        }),
    );
  }

  goToWork(): void {
    const input = this.input();
    const work = input ? workRouteFor(input) : null;

    if (work) {
      this.router.navigate(work.commands, { queryParams: work.queryParams });
    }
  }

  private load(): void {
    this.loading.set(true);
    this.failure.set(null);

    this.subscriptions.push(
      this.inputs
        .getOne(this.slug, this.from())
        .pipe(finalize(() => this.loading.set(false)))
        .subscribe({
          next: (input) => {
            this.input.set(input);
            this.pageTitleService.setTitle(input.name ?? '');

            if (!input.candidateCultures.some((culture) => culture.slug === this.chosenCulture())) {
              this.chosenCulture.set(null);
            }
          },
          error: (failure: CommunityInputError) => this.failure.set(failure),
        }),
    );
  }
}
